import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

import { Button, Icon } from "@swa-ui/core";
import globalRules from "@swa-ui/core/assets/styles/globalRules.module.scss";
import { AutocompleteStationsFormField, stationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./RouteStationsFormFields.module.scss";
import { ROUTE_STATIONS_SCHEMA_FIELD_NAMES } from "./RouteStationsFormFields.schema";

const SWITCHEROO_ALERT_TIMEOUT_DURATION = 2000;

/**
 * RouteStationsFormFields renders a pair of form fields that allows users
 * to enter a departure and arrival station.
 */
export const RouteStationsFormFields = (props) => {
  const { className, disabled, formMethods } = props;
  const { getInternationalStationCode, getStationNameAndState } = stationMethods;
  const { setValue, getValues, trigger } = formMethods;
  const [switcherooAlertContent, setSwitcherooAlertContent] = useState("");
  const internationalStationCodes = useMemo(() => getInternationalStationCode(), []);

  return (
    <div className={classNames(className, styles.routeStationsFormFields)}>
      <AutocompleteStationsFormField {...getDepartFieldProps()} />
      {renderSwitcheroo()}
      <AutocompleteStationsFormField {...getArriveFieldProps()} />
      {renderSwitcherooAlert()}
    </div>
  );

  function renderSwitcheroo() {
    return (
      <Button {...getSwitcherooProps()}>
        <Icon {...getIconProps()} />
      </Button>
    );
  }

  function renderSwitcherooAlert() {
    return (
      <span className={globalRules.hiddenFromScreen} role="alert">
        {switcherooAlertContent}
      </span>
    );
  }

  function getArriveFieldProps() {
    const { originationAirportCode } = getValues();

    return {
      className: styles.arrive,
      componentProps: {
        filteredStations: getFilteredStations(originationAirportCode),
        invalidRouteText: i18n("RouteStationsFormFields__ERROR_MATCHING"),
        labelText: i18n("RouteStationsFormFields__ARRIVE_LABEL"),
        noMatchFoundText: i18n("RouteStationsFormFields__NO_MATCH_FOUND_TEXT"),
        required: true,
      },
      disabled,
      label: i18n("RouteStationsFormFields__ARRIVE_LABEL"),
      name: ROUTE_STATIONS_SCHEMA_FIELD_NAMES.ARRIVE,
    };
  }

  function getDepartFieldProps() {
    const { destinationAirportCode } = getValues();

    return {
      className: styles.depart,
      componentProps: {
        filteredStations: getFilteredStations(destinationAirportCode),
        invalidRouteText: i18n("RouteStationsFormFields__ERROR_MATCHING"),
        labelText: i18n("RouteStationsFormFields__DEPART_LABEL"),
        noMatchFoundText: i18n("RouteStationsFormFields__NO_MATCH_FOUND_TEXT"),
        required: true,
      },
      disabled,
      label: i18n("RouteStationsFormFields__DEPART_LABEL"),
      name: ROUTE_STATIONS_SCHEMA_FIELD_NAMES.DEPART,
    };
  }

  function getIconProps() {
    return {
      "aria-label": i18n("RouteStationsFormFields__SWITCHEROO_ARIA_LABEL"),
      background: {
        color: "route-stations-form-fields-switcheroo-icon-background",
        type: "circle",
      },
      name: "ChangeFlight",
      shrink: 0.75,
      size: "size30",
      transparentBorder: false,
    };
  }

  function getSwitcherooProps() {
    return {
      className: styles.reverseButton,
      disabled,
      onClick: handleSwitcherooClick,
      styleType: "no-style",
    };
  }

  function updateSwitcherooAlertContent() {
    const values = getValues();
    const originationDetails = getAirportDetails(
      values.originationAirportCode,
      "RouteStationsFormFields__DEPART_LABEL"
    );
    const destinationDetails = getAirportDetails(
      values.destinationAirportCode,
      "RouteStationsFormFields__ARRIVE_LABEL"
    );
    const content = [
      originationDetails.code,
      destinationDetails.code,
      originationDetails.label,
      destinationDetails.label,
    ]
      .map((item) => item?.trim())
      .filter(Boolean)
      .join(", ");

    setSwitcherooAlertContent(content);

    setTimeout(() => {
      setSwitcherooAlertContent("");
    }, SWITCHEROO_ALERT_TIMEOUT_DURATION);
  }

  function handleSwitcherooClick() {
    const values = getValues();
    const departValue = values[ROUTE_STATIONS_SCHEMA_FIELD_NAMES.DEPART];
    const arriveValue = values[ROUTE_STATIONS_SCHEMA_FIELD_NAMES.ARRIVE];

    setValue(ROUTE_STATIONS_SCHEMA_FIELD_NAMES.DEPART, arriveValue);
    setValue(ROUTE_STATIONS_SCHEMA_FIELD_NAMES.ARRIVE, departValue);
    trigger(ROUTE_STATIONS_SCHEMA_FIELD_NAMES.DEPART);
    trigger(ROUTE_STATIONS_SCHEMA_FIELD_NAMES.ARRIVE);
    updateSwitcherooAlertContent();
  }

  function getFilteredStations(airportCode) {
    const defaultFilteredStations = Array.isArray(airportCode) ? airportCode : [airportCode];

    return internationalStationCodes.includes(defaultFilteredStations?.[0])
      ? internationalStationCodes
      : defaultFilteredStations;
  }

  function getAirportDetails(airportCode, labelKey) {
    const details = { code: "", label: "" };

    if (airportCode) {
      details.code = ` ${airportCode}`;
      details.label = ` ${i18n(labelKey)} ${getStationNameAndState(airportCode)}`;
    }

    return details;
  }
};

RouteStationsFormFields.propTypes = {
  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Whether to disable form fields. */
  disabled: PropTypes.bool,

  /**
   * Form methods passed down from parent Form, used to inform the parent
   * form and update the form field values when the switcheroo button is clicked.
   */
  formMethods: PropTypes.object.isRequired,
};
